import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Users,
  Plane,
  BedDouble,
  Bus,
  QrCode,
  CheckCircle2,
  CalendarClock,
  Plus,
  MapPin,
  Trash2,
  Megaphone,
  Link2,
  Copy,
  Check,
  Eye,
  EyeOff,
  ClipboardList,
  Bell,
  Download,
  Radio,
  XCircle,
  Clock,
  HeartPulse,
  ScanLine,
  Ticket,
  Upload,
  KeyRound,
  FileSpreadsheet,
} from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post, patch, del, upload, postForm, get, download } from '../../api/client';
import FormBuilder from '../../components/FormBuilder';
import TicketDrop from '../../components/TicketDrop';
import ImportWizard from '../../components/ImportWizard';
import { BulkTicketUpload, TicketQuickView, TicketLegs } from '../../components/TicketTools';
import SessionScanModal from '../../components/SessionScanModal';
import { ScanStats, LiveScanPanel, LiveScanFeed } from '../../components/LiveScans';
import { PartyTag, CheckInActions, usePartyCounts } from '../../components/PartyTag';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import {
  Card, Stat, Button, Modal, Field, Input, Select, Textarea, Badge, StatusBadge,
  Loader, EmptyState, Tabs, SectionTitle, ConfirmModal, Table, PersonCell, SearchInput,
  stagger, fadeUp, label,
} from '../../components/UI';
import { fmtDate, fmtTime, fmtDateTime, pct, EVENT_TYPE_LABEL } from '../../utils/format';

const SESSION_BLANK = {
  title: '', type: 'conference', customType: '', description: '',
  startAt: '', endAt: '', venue: '', isMandatory: false,
  attendanceOpensAt: '', attendanceClosesAt: '',
  customFields: [],
  attendancePoll: {
    enabled: false, question: '', type: 'yes_no', options: [],
    multi: false, allowNote: true, noteLabel: '', closed: false,
  },
};

/** "2026-09-24T19:26" shifted by minutes, staying in the same wall-clock form. */
function shiftLocal(value, minutes) {
  if (!value) return '';
  const [date, time] = String(value).split('T');
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = (time || '00:00').split(':').map(Number);
  const t = new Date(y, mo - 1, d, h, mi + minutes);
  const p = (n) => String(n).padStart(2, '0');
  return `${t.getFullYear()}-${p(t.getMonth() + 1)}-${p(t.getDate())}T${p(t.getHours())}:${p(t.getMinutes())}`;
}

/** The input kinds an organiser can pick for a custom session field. */
const CUSTOM_FIELD_TYPES = [
  ['text', 'Text'],
  ['number', 'Number'],
  ['image', 'Image'],
];

/** Readable names for the schedule row kinds the server accepts. */
/** Room sizes the capacity plan is captured against. */
const OCCUPANCIES = [1, 2, 3, 4];

const SESSION_TYPE_LABEL = {
  conference: 'Conference / Session', outing: 'Outing', breakfast: 'Breakfast',
  lunch: 'Lunch', dinner: 'Dinner', travel: 'Travel', party: 'Party',
  meeting: 'Meeting', other: 'Other (name it below)',
};

export default function EventDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { pathname } = useLocation();
  // These pages are mounted under every portal, so links must stay in the
  // portal the user is actually in.
  const base = `/${pathname.split('/').filter(Boolean)[0] || 'admin'}`;
  const toast = useToast();
  const { isViewer } = useAuth();

  const [tab, setTab] = useState('overview');
  const { data: event, loading, reload } = useFetch(`/events/${id}`);
  const { data: dash } = useFetch(`/events/${id}/dashboard`);
  const { data: catalogue } = useFetch('/events/meta');
  const [savingModules, setSavingModules] = useState(false);
  const [viewerModal, setViewerModal] = useState(false);

  /* Forms + the card pinned to the attendee home screen. */
  const { data: forms, reload: reloadForms } = useFetch('/forms', { event: id, limit: 50 });
  const { data: alerts, reload: reloadAlerts } = useFetch('/home-alerts', { event: id, limit: 50 });
  const [formModal, setFormModal] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [sendCreds, setSendCreds] = useState(false);
  const [sendingCreds, setSendingCreds] = useState(false);
  const [bulkTickets, setBulkTickets] = useState(false);
  const [ticketSel, setTicketSel] = useState(new Map());     // `${flight}|${url}` → { flight, url }
  const [ticketView, setTicketView] = useState(null);
  const [confirmRemoveTickets, setConfirmRemoveTickets] = useState(false);
  const [removingTickets, setRemovingTickets] = useState(false);
  const [ticketFilter, setTicketFilter] = useState('all');   // all | missing
  const [formDraft, setFormDraft] = useState(null);
  const [alertModal, setAlertModal] = useState(false);
  const [alertDraft, setAlertDraft] = useState(null);
  const [qrFor, setQrFor] = useState(null);   // { form, url, image }

  /* Assigning hotels to the event, and the optional capacity plan per hotel. */
  const [hotelModal, setHotelModal] = useState(false);
  const [pickedHotels, setPickedHotels] = useState([]);
  const [inventory, setInventory] = useState([]);
  const { data: allHotels } = useFetch('/hotels', { limit: 200 });

  /* The session's own QR, and who was actually present. */
  const [sessionQrFor, setSessionQrFor] = useState(null);
  const { data: sessionQr, loading: sessionQrLoading } = useFetch(
    sessionQrFor ? `/events/${id}/sessions/${sessionQrFor}/qr` : null, {}, { skip: !sessionQrFor },
  );
  const [attendanceFor, setAttendanceFor] = useState(null);
  const [scanFor, setScanFor] = useState(null);       // session being scanned into
  const [liveFor, setLiveFor] = useState(null);       // session whose scans are being watched
  const { data: attendance, loading: attendanceLoading } = useFetch(
    attendanceFor ? `/events/${id}/sessions/${attendanceFor}/attendance` : null, {}, { skip: !attendanceFor },
  );

  /* Poll responses on one schedule row. */
  const [pollFor, setPollFor] = useState(null);
  const { data: pollData, loading: pollLoading, reload: reloadPoll } = useFetch(
    pollFor ? `/events/${id}/sessions/${pollFor}/responses` : null, {}, { skip: !pollFor },
  );

  /* Who filled a form in, and what they said. */
  const [responsesFor, setResponsesFor] = useState(null);
  /* Who arrived with how many, against the uploaded sheet. */
  const party = usePartyCounts(id);
  /* '' everyone · 'sheet' only the roster · 'check_in' only the walk-ins. */
  const [peopleSource, setPeopleSource] = useState('');
  const { data: responses, loading: responsesLoading, reload: reloadResponses } = useFetch(
    responsesFor ? `/forms/${responsesFor._id}/submissions` : null,
    { limit: 500 }, { skip: !responsesFor },
  );

  const [peopleSearch, setPeopleSearch] = useState('');
  const [issued, setIssued] = useState(null);   // { name, loginId, password }
  const [showPasswords, setShowPasswords] = useState(false);
  const { data: people, loading: peopleLoading, reload: reloadPeople } = useFetch(
    '/users',
    {
      event: id, limit: 200, credentials: 'true',
      ...(peopleSearch ? { search: peopleSearch } : {}),
      ...(peopleSource ? { addedVia: peopleSource } : {}),
    },
  );

  /* Tickets are only offered when the event actually books flights. */
  // Hooks run before the loading guard below, so `event` can still be null here.
  const includesFlights = event?.modules?.flights !== false;
  const { reload: reloadFlights } = useFetch(
    '/flights', { event: id, limit: 400 }, { skip: !includesFlights },
  );
  /* Tickets per person and journey, for the Users table and the bulk tools. */
  const { data: ticketData, reload: reloadTickets } = useFetch(
    '/flights/tickets', { event: id }, { skip: !includesFlights },
  );
  const ticketRowByUser = Object.fromEntries((ticketData?.people || []).map((r) => [String(r.user._id), r]));
  const toggleTicket = (key, item) => setTicketSel((m) => { const n = new Map(m); if (n.has(key)) n.delete(key); else n.set(key, item); return n; });
  const removeSelectedTickets = async () => {
    setRemovingTickets(true);
    try {
      const res = await post('/flights/tickets/bulk-remove', { event: id, tickets: [...ticketSel.values()] });
      toast.success(res.message);
      setTicketSel(new Map()); setConfirmRemoveTickets(false);
      reloadTickets(); reloadFlights();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRemovingTickets(false);
    }
  };

  const uploadTicket = async (person, files) => {
    try {
      const body = new FormData();
      files.forEach((f) => body.append('files', f));
      body.append('event', id);
      body.append('user', person._id);
      const res = await postForm('/flights/upload-ticket', body);
      toast.success(res.message);
      reloadFlights(); reloadTickets();
    } catch (err) {
      toast.error(err.message);
    }
  };

  /* The Registration tab is just the registration-placement forms. */
  const regForms = (forms || []).filter((f) => f.placement === 'registration');
  const [viewer, setViewer] = useState({ name: '', email: '', password: '', mobile: '' });
  const { data: viewers, reload: reloadViewers } = useFetch('/users', { role: 'event_viewer', event: id });

  const [sessionModal, setSessionModal] = useState(false);
  const [session, setSession] = useState(SESSION_BLANK);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [broadcast, setBroadcast] = useState(false);
  const [copied, setCopied] = useState(false);

  if (loading) return <Loader full label="Loading event" />;
  if (!event) return <EmptyState message="Event not found" />;

  const FORM_BLANK = {
    title: '', description: '', placement: 'app_home',
    loginIdentifier: 'mobile', fields: [],
    attendance: { identifier: 'employeeCode', requireCompanions: false, maxCompanions: 0, allowWalkIns: false },
  };
  const ALERT_BLANK = { title: '', message: '', kind: 'form', form: '', scheduleDate: '', showOn: '' };

  const saveForm = async () => {
    if (!formDraft.title.trim()) return toast.error('Give the form a title');
    if (!formDraft.fields.length) return toast.error('Add at least one question');
    if (formDraft.fields.some((f) => !f.label.trim())) return toast.error('Every question needs a label');

    setSaving(true);
    try {
      if (formDraft._id) await patch(`/forms/${formDraft._id}`, formDraft);
      else await post('/forms', { ...formDraft, event: id });
      toast.success(formDraft._id ? 'Form updated' : 'Form created');
      setFormModal(false);
      reloadForms();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setFormStatus = async (form, status) => {
    try {
      await patch(`/forms/${form._id}`, { status });
      toast.success(status === 'published' ? 'Form published' : 'Form closed');
      reloadForms();
    } catch (err) { toast.error(err.message); }
  };

  const deleteForm = async (form) => {
    try {
      await del(`/forms/${form._id}`);
      toast.success('Form deleted');
      reloadForms(); reloadAlerts();
    } catch (err) { toast.error(err.message); }
  };

  /* Passwords are stored as a one-way hash, so a fresh one is generated and
     shown once here — there is no way to read an existing one back. */
  const issuePassword = async (person) => {
    try {
      const res = await post(`/users/${person._id}/password`, { generate: true });
      setIssued({ name: person.name, ...res.data });
      reloadPeople();
    } catch (err) {
      toast.error(err.message);
    }
  };

  /** Login details for everyone on this event, in batches of 25. */
  const sendAllCredentials = async () => {
    setSendingCreds(true);
    try {
      const all = await get('/users', { role: 'user', event: id, limit: 1000 });
      const ids = (Array.isArray(all) ? all : all?.rows || []).map((u) => u._id);
      let sent = 0; const failed = [];
      for (let i = 0; i < ids.length; i += 25) {
        const res = await post('/users/credentials/send', { users: ids.slice(i, i + 25), event: id });
        sent += res.data?.sent || 0;
        failed.push(...(res.data?.failed || []));
      }
      toast[failed.length ? 'error' : 'success'](failed.length ? `Sent to ${sent}; ${failed.length} could not be sent` : `Login details sent to ${sent} people`);
      setSendCreds(false);
      reloadPeople();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSendingCreds(false);
    }
  };

  const downloadPeople = async (format, source = peopleSource) => {
    const slug = event.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const what = source === 'check_in' ? 'added-at-check-in' : source === 'sheet' ? 'from-the-sheet' : 'participants';
    try {
      await download('/users/export', {
        event: id, format, credentials: 'true',
        ...(peopleSearch ? { search: peopleSearch } : {}),
        ...(source ? { addedVia: source } : {}),
      }, `${slug}-${what}.${format}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const downloadAttendance = async (sessionId, title) => {
    try {
      await download(
        `/events/${id}/sessions/${sessionId}/attendance/export`, {},
        `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-attendance.xlsx`,
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  const setPollClosed = async (sessionId, closed) => {
    try {
      const res = await patch(`/events/${id}/sessions/${sessionId}/poll`, { closed });
      toast.success(res.message);
      reloadPoll();
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const downloadPollResponses = async (sessionId, title) => {
    try {
      await download(
        `/events/${id}/sessions/${sessionId}/responses/export`, {},
        `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-responses.xlsx`,
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  const downloadResponses = async (form) => {
    try {
      await download(
        `/forms/${form._id}/submissions/export`, {},
        `${form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-responses.xlsx`,
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openHotels = () => {
    setPickedHotels((event.hotels || []).map((h) => h._id));
    setInventory((event.roomInventory || []).map((r) => ({
      hotel: String(r.hotel), occupancy: r.occupancy, rooms: r.rooms,
    })));
    setHotelModal(true);
  };

  const toggleHotel = (hotelId) => setPickedHotels((list) => (
    list.includes(hotelId) ? list.filter((x) => x !== hotelId) : [...list, hotelId]
  ));

  const setInv = (hotelId, occupancy, rooms) => setInventory((rows) => {
    const rest = rows.filter((r) => !(r.hotel === hotelId && r.occupancy === occupancy));
    const n = Number(rooms);
    return n > 0 ? [...rest, { hotel: hotelId, occupancy, rooms: n }] : rest;
  });

  const invFor = (hotelId, occupancy) => inventory
    .find((r) => r.hotel === hotelId && r.occupancy === occupancy)?.rooms ?? '';

  const saveHotels = async () => {
    setSaving(true);
    try {
      await patch(`/events/${id}`, {
        hotels: pickedHotels,
        // Only keep the plan for hotels still on the event.
        roomInventory: inventory.filter((r) => pickedHotels.includes(r.hotel)),
      });
      toast.success('Hotels updated');
      setHotelModal(false);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const showFormQr = async (form) => {
    setQrFor({ form, loading: true });
    try {
      const res = await get(`/forms/${form._id}/qr`);
      setQrFor({ form, ...res });
    } catch (err) {
      toast.error(err.message);
      setQrFor(null);
    }
  };

  const saveAlert = async () => {
    if (!alertDraft.title.trim()) return toast.error('Give the alert a title');
    if (!alertDraft.showOn) return toast.error('Pick the date it should appear');
    if (alertDraft.kind === 'form' && !alertDraft.form) return toast.error('Choose the form to show');
    if (alertDraft.kind === 'schedule' && !alertDraft.scheduleDate) return toast.error('Choose which day to show');

    setSaving(true);
    try {
      if (alertDraft._id) await patch(`/home-alerts/${alertDraft._id}`, alertDraft);
      else await post('/home-alerts', { ...alertDraft, event: id });
      toast.success('Home alert saved');
      setAlertModal(false);
      reloadAlerts();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setAlertStatus = async (alert, status) => {
    try {
      await patch(`/home-alerts/${alert._id}`, { status });
      toast.success(status === 'published' ? 'Pinned to the home screen' : 'Unpinned');
      reloadAlerts();
    } catch (err) { toast.error(err.message); }
  };

  const deleteAlert = async (alert) => {
    try { await del(`/home-alerts/${alert._id}`); toast.success('Alert removed'); reloadAlerts(); }
    catch (err) { toast.error(err.message); }
  };

  const setPoll = (patchObj) => setSession((s) => ({
    ...s, attendancePoll: { ...s.attendancePoll, ...patchObj },
  }));

  const addCustomField = () => setSession((s) => ({
    ...s, customFields: [...s.customFields, { label: '', type: 'text', value: '' }],
  }));

  const setCustomField = (i, patch) => setSession((s) => ({
    ...s,
    customFields: s.customFields.map((f, n) => (n === i ? { ...f, ...patch } : f)),
  }));

  const removeCustomField = (i) => setSession((s) => ({
    ...s, customFields: s.customFields.filter((_, n) => n !== i),
  }));

  /* An image field stores a URL, so the file is uploaded before the save. */
  const uploadFieldImage = async (i, file) => {
    if (!file) return;
    setCustomField(i, { uploading: true });
    try {
      const res = await upload(`/events/${id}/session-asset`, file);
      setCustomField(i, { value: res.data.url, uploading: false });
    } catch (err) {
      toast.error(err.message);
      setCustomField(i, { uploading: false });
    }
  };

  const addViewer = async () => {
    if (!viewer.name.trim() || !viewer.email.trim() || !viewer.password) {
      return toast.error('Name, email and password are required');
    }
    if (viewer.password.length < 6) return toast.error('Password must be at least 6 characters');
    setSaving(true);
    try {
      const res = await post('/users/event-viewer', { ...viewer, event: id });
      toast.success(res.message);
      setViewerModal(false);
      setViewer({ name: '', email: '', password: '', mobile: '' });
      reloadViewers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleModule = async (key, value) => {
    setSavingModules(true);
    try {
      // The server re-applies the parent/child rules, so we just send the change.
      await patch(`/events/${id}`, { modules: { [key]: value } });
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingModules(false);
    }
  };

  const addSession = async () => {
    if (!session.title.trim() || !session.startAt) {
      return toast.error('A session needs a title and a start time');
    }
    if (session.type === 'other' && !session.customType.trim()) {
      return toast.error('Give this schedule type a name');
    }
    const poll = session.attendancePoll;
    if (poll.enabled && poll.type === 'choice' && poll.options.filter((o) => o.trim()).length < 2) {
      return toast.error('A choice poll needs at least two options');
    }
    setSaving(true);
    try {
      await post(`/events/${id}/sessions`, {
        ...session,
        customFields: session.customFields.filter((f) => f.label.trim()),
        attendancePoll: {
          ...session.attendancePoll,
          options: session.attendancePoll.options.map((o) => o.trim()).filter(Boolean),
        },
      });
      toast.success('Session added to the schedule');
      setSessionModal(false);
      setSession(SESSION_BLANK);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeSession = async () => {
    try {
      await del(`/events/${id}/sessions/${confirmDel}`);
      toast.success('Session removed');
      setConfirmDel(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const changeStatus = async (status) => {
    try {
      await patch(`/events/${id}`, { status });
      toast.success(`Event marked ${status}`);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const days = groupByDay(event.sessions || []);

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      <Button variant="subtle" size="sm" icon={ArrowLeft} onClick={() => nav(`${base}/events`)} style={{ marginBottom: 14 }}>
        All events
      </Button>

      {/* ── hero ─────────────────────────────────────────────────── */}
      <Card padding={0} style={{ overflow: 'hidden', marginBottom: 20 }}>
        <div style={{
          minHeight: 152, padding: '26px 26px 22px', position: 'relative',
          background: event.banner
            ? `linear-gradient(180deg, rgba(11,11,12,0.2), rgba(11,11,12,0.9)), url(${event.banner}) center/cover`
            : 'linear-gradient(125deg, #16161C 0%, #202028 55%, #131318 100%)',
        }}>
          <motion.div
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute', top: -40, right: -20, width: 300, height: 300,
              background: 'radial-gradient(circle, rgba(239,212,138,0.13), transparent 70%)',
              filter: 'blur(40px)', pointerEvents: 'none',
            }}
          />
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 11, flexWrap: 'wrap' }}>
                <Badge tone="gold">{EVENT_TYPE_LABEL[event.type] || event.type}</Badge>
                <StatusBadge status={event.status} />
                {event.code && <Badge tone="neutral">{event.code}</Badge>}
              </div>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 600, lineHeight: 1.2 }}>
                {event.title}
              </h1>
              <div style={{ display: 'flex', gap: 18, marginTop: 12, flexWrap: 'wrap', fontSize: 13, color: 'var(--text-mid)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <CalendarClock size={14} color="var(--gold-500)" />
                  {fmtDate(event.startDate)} – {fmtDate(event.endDate)}
                </span>
                {event.venue?.name && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <MapPin size={14} color="var(--gold-500)" />
                    {event.venue.name}{event.venue.city ? `, ${event.venue.city}` : ''}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <Button variant="ghost" size="sm" icon={Megaphone} onClick={() => setBroadcast(true)}>Broadcast</Button>
              <Select value={event.status} onChange={(e) => changeStatus(e.target.value)} style={{ width: 138, padding: '7px 30px 7px 11px', fontSize: 12.5 }}>
                {['draft', 'published', 'live', 'completed', 'cancelled'].map((s) => (
                  <option key={s} value={s}>{label(s)}</option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </Card>

      {/* ── stats ────────────────────────────────────────────────── */}
      {dash && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 13, marginBottom: 20 }}>
          <Stat label="Participants" value={dash.participants.total} icon={Users} tone="info" sub={`${dash.participants.vips} VIP`} />
          <Stat label="Checked In" value={dash.participants.checkedIn} icon={QrCode} tone="success"
            progress={pct(dash.participants.checkedIn, dash.participants.total)} />
          <Stat label="Tickets" value={dash.travel.ticketsIssued} icon={Plane} tone="gold"
            sub={`${dash.travel.docsPending} docs pending`} />
          <Stat label="Rooms" value={`${dash.stay.roomsOccupied}/${dash.stay.roomsTotal}`} icon={BedDouble} tone="warning"
            progress={pct(dash.stay.roomsOccupied, dash.stay.roomsTotal)} />
          <Stat label="Coaches" value={dash.transport.busesTotal} icon={Bus} tone="info" sub={`${dash.transport.boarded} boarded`} />
        </div>
      )}

      {/* Scans as they come in — each tile opens the list behind it. */}
      <ScanStats eventId={id} hasModule={(k) => event.modules?.[k] !== false} readOnly={isViewer} />

      <Tabs
        active={tab} onChange={setTab}
        tabs={[
          { key: 'overview', label: 'Overview', icon: MapPin },
          { key: 'schedule', label: 'Schedule', icon: CalendarClock, count: event.sessions?.length || 0 },
          { key: 'people', label: 'Users', icon: Users, count: dash?.participants?.total || 0 },
          { key: 'forms', label: 'Forms & Alerts', icon: ClipboardList, count: forms?.length || 0 },
          { key: 'registration', label: 'Registration', icon: Link2 },
          { key: 'health', label: 'Health & emergency', icon: HeartPulse },
        ]}
      />

      {/* ── overview ─────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          <LiveScanPanel eventId={id} />
          <Card>
            <SectionTitle title="About this event" />
            <p style={{ fontSize: 13.5, color: 'var(--text-mid)', lineHeight: 1.72 }}>
              {event.description || 'No description has been added yet.'}
            </p>
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Row label="Event code" value={event.code || '—'} />
              <Row label="Timezone" value={event.timezone} />
              <Row label="Location" value={event.venue?.name ? `${event.venue.name}${event.venue.city ? `, ${event.venue.city}` : ''}` : '—'} />
              <Row label="Address" value={event.venue?.address || '—'} />
            </div>
          </Card>

          <Card>
            <SectionTitle title="Partners" />
            <p style={{ fontSize: 11.5, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600, marginBottom: 9 }}>
              Corporates
            </p>
            {event.corporates?.length ? (
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                {event.corporates.map((c) => <Badge key={c._id} tone="gold">{c.name}</Badge>)}
              </div>
            ) : <p style={{ fontSize: 13, color: 'var(--text-lo)' }}>None linked yet</p>}

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, margin: '20px 0 9px',
            }}>
              <p style={{ fontSize: 11.5, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600 }}>
                Hotels
              </p>
              {!isViewer && (
                <Button size="sm" variant="subtle" icon={Plus} onClick={openHotels}>
                  {event.hotels?.length ? 'Manage' : 'Assign'}
                </Button>
              )}
            </div>

            {event.hotels?.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {event.hotels.map((h) => {
                  const plan = (event.roomInventory || []).filter((r) => String(r.hotel) === String(h._id));
                  const beds = plan.reduce((n, r) => n + r.occupancy * r.rooms, 0);
                  return (
                    <div key={h._id} style={{
                      padding: '11px 13px', background: 'var(--ink-800)',
                      border: '1px solid var(--border)', borderRadius: 'var(--r)',
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{h.name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 3 }}>
                        Check-in {h.checkInTime || '14:00'} · Check-out {h.checkOutTime || '12:00'}
                      </div>
                      {plan.length > 0 && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 9 }}>
                          {plan.sort((a, b) => a.occupancy - b.occupancy).map((r) => (
                            <Badge key={r.occupancy} tone="neutral">
                              {r.rooms} × {r.occupancy}-person
                            </Badge>
                          ))}
                          <Badge tone="gold">{beds} beds</Badge>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : <p style={{ fontSize: 13, color: 'var(--text-lo)' }}>No hotels assigned yet</p>}
          </Card>

          <Card>
            <SectionTitle
              title="What this event includes"
              subtitle="Unticking one hides it from the app, the portals and the API"
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(catalogue?.modules || []).map((m) => {
                const on = event.modules?.[m.key] !== false;
                const parentOff = m.requires && event.modules?.[m.requires] === false;
                return (
                  <label key={m.key} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 6px',
                    borderRadius: 'var(--r-sm)', marginLeft: m.requires ? 20 : 0,
                    cursor: parentOff || savingModules ? 'not-allowed' : 'pointer',
                    opacity: parentOff ? 0.45 : 1,
                  }}>
                    <input
                      type="checkbox" checked={on} disabled={parentOff || savingModules}
                      onChange={() => toggleModule(m.key, !on)}
                      style={{ accentColor: 'var(--gold-400)', marginTop: 2 }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13, display: 'block', color: on ? 'var(--text-hi)' : 'var(--text-lo)' }}>
                        {m.label}
                      </span>
                      <span style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{m.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </Card>

          <Card>
            <SectionTitle
              title="Company access"
              subtitle="Observers see every screen for this event and can change nothing"
              action={<Button icon={Plus} size="sm" onClick={() => setViewerModal(true)}>Add observer</Button>}
            />
            {viewers?.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {viewers.map((v) => (
                  <div key={v._id} style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <Eye size={15} color="var(--gold-500)" style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13, display: 'block' }}>{v.name}</span>
                      <span style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{v.email}</span>
                    </span>
                    <Badge tone="neutral">View only</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text-lo)' }}>
                No company observers yet. Add one so the client can follow the event without editing it.
              </p>
            )}
          </Card>
        </div>
      )}

      {/* ── users on this event ──────────────────────────────────── */}
      {tab === 'people' && (
        <>
          <SectionTitle
            title="Users"
            subtitle="Everyone registered for this event, and how each of them signs in"
          />

          <div style={{ display: 'flex', gap: 11, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <SearchInput
              value={peopleSearch}
              onChange={(e) => setPeopleSearch(e.target.value)}
              placeholder="Search by name, email, mobile or employee code…"
              style={{ flex: 1, minWidth: 260, maxWidth: 420 }}
            />
            <Button
              variant="subtle" size="sm" icon={showPasswords ? EyeOff : Eye}
              onClick={() => setShowPasswords((v) => !v)}
            >
              {showPasswords ? 'Hide passwords' : 'Show passwords'}
            </Button>
            <Button variant="outline" size="sm" icon={Download} onClick={() => downloadPeople('xlsx')}>
              Excel
            </Button>
            <Button variant="outline" size="sm" icon={Download} onClick={() => downloadPeople('pdf')}>
              PDF
            </Button>
            <Button variant="outline" size="sm" icon={Upload} onClick={() => setImportOpen(true)}>
              Import sheet
            </Button>
            <Button variant="outline" size="sm" icon={FileSpreadsheet} onClick={() => nav(`${base}/events/${id}/sheet`)}>
              Open full sheet
            </Button>
            <Button size="sm" icon={KeyRound} onClick={() => setSendCreds(true)}>
              Send login details
            </Button>
            {includesFlights && !isViewer && (
              <Button variant="outline" size="sm" icon={Ticket} onClick={() => setBulkTickets(true)}>
                Upload tickets
              </Button>
            )}
            {ticketSel.size > 0 && (
              <Button variant="danger" size="sm" icon={Trash2} onClick={() => setConfirmRemoveTickets(true)}>
                Remove {ticketSel.size} ticket{ticketSel.size === 1 ? '' : 's'}
              </Button>
            )}
          </div>

          {includesFlights && ticketData?.summary && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14, fontSize: 12.5 }}>
              <Badge tone="success">{ticketData.summary.withBoth} with both tickets</Badge>
              <Badge tone={ticketData.summary.journeysWithoutTicket ? 'warning' : 'neutral'}>{ticketData.summary.journeysWithoutTicket} journeys still without a ticket</Badge>
              <Badge tone={ticketData.summary.withNone ? 'danger' : 'neutral'}>{ticketData.summary.withNone} people with none</Badge>
              <button type="button" onClick={() => setTicketFilter(ticketFilter === 'all' ? 'missing' : 'all')}
                style={{ fontSize: 12.5, color: 'var(--gold-300)', marginLeft: 4 }}>
                {ticketFilter === 'all' ? 'Show only people missing a ticket' : 'Show everyone'}
              </button>
            </div>
          )}

          {(party.totals?.walkIns > 0 || peopleSource) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
              {[['', 'Everyone'], ['sheet', 'From the uploaded sheet'], ['check_in', `Added at check-in${party.totals?.walkIns ? ` (${party.totals.walkIns})` : ''}`]].map(([v, label]) => (
                <button
                  key={v || 'all'} type="button" onClick={() => setPeopleSource(v)}
                  style={{
                    padding: '6px 13px', borderRadius: 99, fontSize: 12.5, fontWeight: 600,
                    border: `1px solid ${peopleSource === v ? 'var(--gold-400)' : 'var(--border)'}`,
                    background: peopleSource === v ? 'var(--gold-400)' : 'transparent',
                    color: peopleSource === v ? 'var(--ink-900)' : 'var(--text-mid)',
                  }}
                >
                  {label}
                </button>
              ))}
              {party.totals?.walkIns > 0 && (
                <Button variant="subtle" size="sm" icon={Download} onClick={() => downloadPeople('xlsx', 'check_in')}>
                  Walk-ins Excel
                </Button>
              )}
            </div>
          )}

          {party.totals && (party.asking || party.totals.companions > 0) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14, fontSize: 12.5 }}>
              <Badge tone="gold">{party.totals.people} people in — {party.totals.checkedIn} checked in, {party.totals.companions} with them</Badge>
              <Badge tone="neutral">{party.totals.expectedPeople} expected from the sheet</Badge>
              {party.totals.up > 0 && <Badge tone="warning">{party.totals.up} brought more than the sheet said</Badge>}
              {party.totals.down > 0 && <Badge tone="success">{party.totals.down} brought fewer</Badge>}
              {party.totals.pending > 0 && <Badge tone="neutral">{party.totals.pending} still to check in</Badge>}
            </div>
          )}

          <ImportWizard
            open={importOpen} onClose={() => setImportOpen(false)} eventId={id}
            onImported={() => { reloadPeople(); reloadFlights(); reloadTickets(); }}
            onOpenSheet={(sheetId) => nav(`${base}/events/${id}/sheet?sheet=${sheetId}`)}
          />
          <BulkTicketUpload open={bulkTickets} onClose={() => setBulkTickets(false)} eventId={id} onAttached={() => { reloadTickets(); reloadFlights(); }} />
          {ticketView && <TicketQuickView ticket={ticketView} onClose={() => setTicketView(null)} />}
          <ConfirmModal
            open={confirmRemoveTickets} onClose={() => setConfirmRemoveTickets(false)} onConfirm={removeSelectedTickets} loading={removingTickets} danger
            title="Remove these tickets?" confirmLabel="Remove"
            message={`${ticketSel.size} ticket${ticketSel.size === 1 ? '' : 's'} will be taken off ${ticketSel.size === 1 ? 'the' : 'their'} journeys and deleted. The people are not notified.`}
          />
          <ConfirmModal
            open={sendCreds} onClose={() => setSendCreds(false)} onConfirm={sendAllCredentials} loading={sendingCreds}
            title="Send login details" confirmLabel="Send to everyone"
            message="Everyone on this event gets a WhatsApp message and an email (whichever they have) with the web link, their login id and password. People who already have a password we issued get the same one again; people who set their own keep it."
          />

          <Table
            loading={peopleLoading}
            rows={ticketFilter === 'missing'
              ? (people || []).filter((r) => { const t = ticketRowByUser[String(r._id)]; return !t || ['onward', 'return'].some((d) => !t[d]?.files?.length); })
              : (people || [])}
            empty="Nobody has registered for this event yet"
            columns={[
              {
                key: 'name',
                title: 'Person',
                render: (r) => (
                  <div>
                    <PersonCell person={r} />
                    {r.addedVia === 'check_in' && (
                      <Badge tone="gold" style={{ marginTop: 4 }}>Added at check-in</Badge>
                    )}
                    {(r.employeeCode || r.officeLocation) && (
                      <p style={{ fontSize: 11.5, color: 'var(--text-mid)', marginTop: 3 }}>
                        {[r.employeeCode && `#${r.employeeCode}`, r.officeLocation].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {r.extra && Object.keys(r.extra).length > 0 && (
                      <p style={{ fontSize: 11, color: 'var(--text-lo)', marginTop: 4, maxWidth: 260 }}>
                        {Object.entries(r.extra).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                      </p>
                    )}
                  </div>
                ),
              },
              {
                key: 'email',
                title: 'Login ID',
                render: (r) => (r.email
                  ? <span style={{ fontSize: 12.5, color: 'var(--gold-300)' }}>{r.email}</span>
                  : <span style={{ fontSize: 12.5, color: 'var(--text-lo)' }}>No email</span>),
              },
              { key: 'mobile', title: 'Mobile', render: (r) => r.mobile || '—' },
              {
                key: 'manager',
                title: 'Reports to',
                render: (r) => {
                  const name = r.manager?.name || r.managerName;
                  if (!name) return <span style={{ color: 'var(--text-lo)' }}>—</span>;
                  const id = r.manager?.employeeCode || r.managerCode;
                  const reach = r.manager?.mobile || r.managerMobile || r.manager?.email || r.managerEmail;
                  return (
                    <div style={{ maxWidth: 190 }}>
                      <p style={{ fontSize: 13 }}>{name}{r.manager ? '' : ' *'}</p>
                      <p style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{[id && `#${id}`, reach].filter(Boolean).join(' · ')}</p>
                    </div>
                  );
                },
              },
              {
                key: 'signin',
                title: 'Signs in with',
                render: (r) => (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {r.mobile && <Badge tone="neutral">Mobile OTP</Badge>}
                    {r.hasPassword || r.passwordSetAt
                      ? <Badge tone="success" dot>Email + password</Badge>
                      : r.email ? <Badge tone="neutral">No password set</Badge> : null}
                  </div>
                ),
              },
              {
                key: 'issuedPassword',
                title: 'Password',
                render: (r) => {
                  if (!r.issuedPassword) {
                    // A password may exist that we genuinely cannot read back:
                    // set before this was stored, or chosen by the person.
                    return (
                      <span style={{ fontSize: 12, color: 'var(--text-lo)' }}>
                        {r.hasPassword ? 'Set — not recoverable' : '—'}
                      </span>
                    );
                  }
                  return (
                    <button
                      onClick={() => {
                        navigator.clipboard?.writeText(r.issuedPassword);
                        toast.success('Password copied');
                      }}
                      title="Click to copy"
                      style={{
                        fontFamily: 'monospace', fontSize: 12.5, color: 'var(--gold-300)',
                        letterSpacing: showPasswords ? 0 : 2,
                      }}
                    >
                      {showPasswords ? r.issuedPassword : '•'.repeat(r.issuedPassword.length)}
                    </button>
                  );
                },
              },
              ...(includesFlights ? [{
                key: 'ticket',
                title: 'Tickets',
                render: (r) => (
                  <div>
                    <TicketLegs
                      row={ticketRowByUser[String(r._id)]}
                      person={r}
                      selected={ticketSel}
                      onToggle={toggleTicket}
                      onView={setTicketView}
                      readOnly={isViewer}
                    />
                    <TicketDrop
                      person={r}
                      tickets={[]}
                      readOnly={isViewer}
                      onUpload={uploadTicket}
                    />
                  </div>
                ),
              }] : []),
              ...(party.asking || party.rows.some((x) => x.expected !== null) ? [{
                key: 'party',
                title: 'People with them',
                render: (r) => (
                  <div>
                    <PartyTag row={party.byUser[String(r._id)]} expected={r.expectedCompanions ?? null} />
                    {!isViewer && (
                      <CheckInActions
                        eventId={id} person={r} row={party.byUser[String(r._id)]}
                        onChange={() => { party.reload(); reloadPeople(); }}
                      />
                    )}
                  </div>
                ),
              }] : []),
              {
                key: 'lastLoginAt',
                title: 'Last active',
                render: (r) => (r.lastLoginAt ? fmtDate(r.lastLoginAt) : 'Never'),
              },
              {
                key: 'action',
                title: '',
                align: 'right',
                render: (r) => (r.email ? (
                  <Button size="sm" variant="subtle" onClick={() => issuePassword(r)}>
                    {r.hasPassword || r.passwordSetAt ? 'Reset password' : 'Generate password'}
                  </Button>
                ) : null),
              },
            ]}
          />

          {includesFlights && (
            <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 14, lineHeight: 1.7, maxWidth: 680 }}>
              Use <strong>Upload tickets</strong> to drop a whole folder of PDFs — each is read and matched to
              its passenger by PNR or name, and you confirm before anything is attached. For one person, drag a
              ticket onto their row or use Upload / the camera. The names printed on each ticket are shown under
              it so a wrong match stands out; tick tickets and use Remove to take them off in bulk.
            </p>
          )}

          <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 14, lineHeight: 1.7, maxWidth: 680 }}>
            A name followed by <strong>*</strong> under "Reports to" came from the sheet but has no account on this event yet —
            import the manager too and the two link up automatically.
            Passwords shown here are the ones issued by you or generated at
            registration, and are the same as what was sent out. Once someone
            changes their own password this column reads "Changed by them" — what
            they chose is hashed and cannot be shown. Hotel and observer accounts
            never see this column.
          </p>
        </>
      )}

      {/* ── forms & home-screen alerts ───────────────────────────── */}
      {tab === 'forms' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div>
            <SectionTitle
              title="Forms"
              subtitle="Questions you can pin to the app home screen or spread as a public link"
              action={(
                <Button icon={Plus} size="sm"
                  onClick={() => { setFormDraft({ ...FORM_BLANK }); setFormModal(true); }}>
                  New form
                </Button>
              )}
            />
            {!forms?.length ? (
              <EmptyState
                message="No forms yet"
                hint="Build one to collect dietary needs, ID proof or anything else you need before the event."
                icon={ClipboardList}
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {forms.map((f) => (
                  <Card key={f._id} padding={15}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 600 }}>{f.title}</span>
                          <Badge tone={f.status === 'published' ? 'success' : f.status === 'closed' ? 'neutral' : 'warning'}>
                            {f.status}
                          </Badge>
                          <Badge tone={f.placement === 'attendance' ? 'gold' : 'neutral'}>
                            {f.placement === 'attendance' ? 'asked at check-in' : f.placement.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 4 }}>
                          {f.fields.length} question{f.fields.length === 1 ? '' : 's'}
                          {' · '}{f.submissionCount || 0} response{f.submissionCount === 1 ? '' : 's'}
                        </p>
                      </div>
                      {f.submissionCount > 0 && (
                        <Button variant="outline" size="sm" icon={Users} onClick={() => setResponsesFor(f)}>
                          Responses ({f.submissionCount})
                        </Button>
                      )}
                      {['registration', 'attendance'].includes(f.placement) && f.status === 'published' && (
                        <Button variant="outline" size="sm" icon={Link2} onClick={() => showFormQr(f)}>
                          Link & QR
                        </Button>
                      )}
                      <Button variant="subtle" size="sm"
                        onClick={() => { setFormDraft({ ...f, fields: [...f.fields] }); setFormModal(true); }}>
                        Edit
                      </Button>
                      {f.status === 'published' ? (
                        <Button variant="subtle" size="sm" onClick={() => setFormStatus(f, 'closed')}>Close</Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setFormStatus(f, 'published')}>Publish</Button>
                      )}
                      <button onClick={() => deleteForm(f)} title="Delete form"
                        style={{ color: 'var(--danger)', padding: 6, display: 'flex' }}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <div>
            <SectionTitle
              title="Home screen alerts"
              subtitle="The card attendees see on their app home — a form to fill in, or a day's schedule to confirm"
              action={(
                <Button icon={Plus} size="sm"
                  onClick={() => { setAlertDraft({ ...ALERT_BLANK }); setAlertModal(true); }}>
                  New alert
                </Button>
              )}
            />
            {!alerts?.length ? (
              <EmptyState
                message="Nothing pinned to the home screen"
                hint="Add an alert to ask attendees for something, or to have them confirm a day's plans."
                icon={Bell}
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {alerts.map((a) => (
                  <Card key={a._id} padding={15}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 600 }}>{a.title}</span>
                          <Badge tone={a.status === 'published' ? 'success' : 'warning'}>{a.status}</Badge>
                          <Badge tone="info">{a.kind === 'form' ? 'Form' : 'Schedule'}</Badge>
                        </div>
                        <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 4 }}>
                          Shows from {fmtDate(a.showOn)}
                          {a.kind === 'form' && a.form ? ` · ${a.form.title}` : ''}
                          {a.kind === 'schedule' ? ` · ${a.scheduleDate}` : ''}
                        </p>
                      </div>
                      <Button variant="subtle" size="sm"
                        onClick={() => {
                          setAlertDraft({
                            ...a,
                            form: a.form?._id || a.form || '',
                            showOn: a.showOn ? a.showOn.slice(0, 10) : '',
                          });
                          setAlertModal(true);
                        }}>
                        Edit
                      </Button>
                      {a.status === 'published' ? (
                        <Button variant="subtle" size="sm" onClick={() => setAlertStatus(a, 'draft')}>Unpin</Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setAlertStatus(a, 'published')}>Pin</Button>
                      )}
                      <button onClick={() => deleteAlert(a)} title="Remove alert"
                        style={{ color: 'var(--danger)', padding: 6, display: 'flex' }}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── schedule ─────────────────────────────────────────────── */}
      {tab === 'schedule' && (
        <>
          <SectionTitle
            title="Programme schedule"
            subtitle="Each session gets its own QR for attendance scanning"
            action={<Button icon={Plus} size="sm" onClick={() => setSessionModal(true)}>Add session</Button>}
          />

          {!days.length ? (
            <EmptyState
              message="No sessions scheduled"
              hint="Add keynotes, workshops and dinners so attendees see the agenda in their app."
              icon={CalendarClock}
              action={<Button size="sm" icon={Plus} onClick={() => setSessionModal(true)}>Add first session</Button>}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
              {days.map(([date, list], di) => (
                <div key={date}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 12 }}>
                    <div style={{
                      padding: '5px 12px', borderRadius: 99,
                      background: 'rgba(239,212,138,0.12)', color: 'var(--gold-400)',
                      fontSize: 12, fontWeight: 600,
                    }}>
                      Day {di + 1}
                    </div>
                    <span style={{ fontSize: 13, color: 'var(--text-mid)' }}>{fmtDate(date)}</span>
                    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {list.map((s, i) => (
                      <motion.div key={s._id} variants={fadeUp} initial="hidden" animate="show" transition={{ delay: i * 0.04 }}>
                        <Card padding={16} hover style={{ display: 'flex', gap: 15, alignItems: 'flex-start' }}>
                          <div style={{
                            minWidth: 74, textAlign: 'center', padding: '9px 6px',
                            background: 'var(--ink-800)', borderRadius: 'var(--r-sm)',
                            border: '1px solid var(--border)', flexShrink: 0,
                          }}>
                            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--gold-400)' }}>{fmtTime(s.startAt)}</div>
                            {s.endAt && <div style={{ fontSize: 10.5, color: 'var(--text-lo)', marginTop: 2 }}>to {fmtTime(s.endAt)}</div>}
                          </div>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                              <h4 style={{ fontSize: 14, fontWeight: 600 }}>{s.title}</h4>
                              {s.isMandatory && <Badge tone="danger">Mandatory</Badge>}
                              {(s.customType || s.type) && <Badge tone="info">{s.customType || s.type}</Badge>}
                            </div>
                            {s.description && (
                              <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 5, lineHeight: 1.6 }}>{s.description}</p>
                            )}
                            <div style={{ display: 'flex', gap: 15, marginTop: 8, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-lo)' }}>
                              {(s.hall || s.venue) && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                  <MapPin size={12} /> {s.hall || s.venue}
                                </span>
                              )}
                              {s.customFields?.filter((f) => f.type !== 'image' && f.value).map((f) => (
                                <span key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                  <span style={{ color: 'var(--text-lo)' }}>{f.label}:</span> {f.value}
                                </span>
                              ))}
                              {s.speakers?.length > 0 && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                  <Users size={12} /> {s.speakers.join(', ')}
                                </span>
                              )}
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                            {!isViewer && (
                              <Button size="sm" icon={ScanLine} onClick={() => setScanFor(s)}>
                                Scan attendees
                              </Button>
                            )}
                            <Button size="sm" variant="subtle" icon={QrCode} onClick={() => setSessionQrFor(s._id)}>
                              QR
                            </Button>
                            <Button size="sm" variant="subtle" icon={CheckCircle2} onClick={() => setAttendanceFor(s._id)}>
                              Attendance
                            </Button>
                            <Button size="sm" variant="subtle" icon={Radio} onClick={() => setLiveFor(s)}>
                              Live
                            </Button>
                            {s.attendancePoll?.enabled && (
                              <Button size="sm" variant="subtle" icon={Users} onClick={() => setPollFor(s._id)}>
                                Responses
                              </Button>
                            )}
                            <button onClick={() => setConfirmDel(s._id)} title="Remove session"
                              style={{ color: 'var(--text-lo)', padding: 6, borderRadius: 6 }}>
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── registration ─────────────────────────────────────────── */}
      {tab === 'registration' && (
        <>
          <SectionTitle
            title="Open registration"
            subtitle="Public links people outside your uploaded list can register through"
            action={(
              <Button icon={Plus} size="sm"
                onClick={() => {
                  setFormDraft({ ...FORM_BLANK, placement: 'registration' });
                  setFormModal(true);
                }}>
                New registration form
              </Button>
            )}
          />

          {!regForms.length ? (
            <EmptyState
              message="No registration link yet"
              hint="Build a form, choose whether people sign in by mobile or email, then publish it to get a link and QR code."
              icon={Link2}
              action={(
                <Button icon={Plus}
                  onClick={() => {
                    setFormDraft({ ...FORM_BLANK, placement: 'registration' });
                    setFormModal(true);
                  }}>
                  Build the form
                </Button>
              )}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {regForms.map((f) => (
                <Card key={f._id}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: 15.5, fontWeight: 600 }}>{f.title}</h3>
                        <Badge tone={f.status === 'published' ? 'success' : f.status === 'closed' ? 'neutral' : 'warning'} dot>
                          {f.status === 'published' ? 'Live' : f.status}
                        </Badge>
                      </div>
                      <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 5 }}>
                        {f.fields.length} question{f.fields.length === 1 ? '' : 's'}
                        {' · '}{f.submissionCount || 0} registration{f.submissionCount === 1 ? '' : 's'}
                        {' · sign in by '}
                        <strong style={{ color: 'var(--gold-400)' }}>
                          {f.loginIdentifier === 'email' ? 'email + password' : 'mobile OTP'}
                        </strong>
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {f.status === 'published' && (
                        <Button size="sm" variant="outline" icon={Link2} onClick={() => showFormQr(f)}>
                          Link & QR
                        </Button>
                      )}
                      <Button size="sm" variant="subtle"
                        onClick={() => { setFormDraft({ ...f, fields: [...f.fields] }); setFormModal(true); }}>
                        Edit
                      </Button>
                      {f.status === 'published' ? (
                        <Button size="sm" variant="subtle" onClick={() => setFormStatus(f, 'closed')}>Close</Button>
                      ) : (
                        <Button size="sm" onClick={() => setFormStatus(f, 'published')}>Publish</Button>
                      )}
                    </div>
                  </div>

                  {f.status !== 'published' && (
                    <p style={{
                      fontSize: 12, color: 'var(--warn)', marginTop: 13, paddingTop: 13,
                      borderTop: '1px solid var(--border)', lineHeight: 1.6,
                    }}>
                      This form is {f.status}. Publish it to generate the public link and QR code.
                    </p>
                  )}

                  <div style={{ marginTop: 15, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                    <p style={{
                      fontSize: 11.5, color: 'var(--text-lo)', textTransform: 'uppercase',
                      letterSpacing: 0.9, fontWeight: 600, marginBottom: 10,
                    }}>
                      What it asks for
                    </p>
                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                      {f.fields.map((fl) => (
                        <Badge key={fl.key} tone={fl.required ? 'gold' : 'neutral'}>
                          {fl.label}{fl.required ? ' *' : ''}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Card>
              ))}

              <p style={{ fontSize: 12, color: 'var(--text-lo)', lineHeight: 1.7 }}>
                Capacity for this event is{' '}
                <strong style={{ color: 'var(--text-mid)' }}>
                  {event.capacity > 0 ? `${event.capacity} seats` : 'unlimited'}
                </strong>
                . Registrations are refused once it is reached.
              </p>
            </div>
          )}
        </>
      )}

      {/* ── modals ───────────────────────────────────────────────── */}
      <Modal
        open={sessionModal} onClose={() => setSessionModal(false)}
        title="Add a session" subtitle="It appears in the attendee app immediately"
        footer={
          <>
            <Button variant="subtle" onClick={() => setSessionModal(false)}>Cancel</Button>
            <Button onClick={addSession} loading={saving}>Add session</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <Field label="Session title" required>
            <Input value={session.title} onChange={(e) => setSession({ ...session, title: e.target.value })}
              placeholder="e.g. Opening Keynote" autoFocus />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Type">
              <Select value={session.type} onChange={(e) => setSession({ ...session, type: e.target.value })}>
                {(catalogue?.sessionTypes || Object.keys(SESSION_TYPE_LABEL)).map((t) => (
                  <option key={t} value={t}>{SESSION_TYPE_LABEL[t] || t}</option>
                ))}
              </Select>
            </Field>
            {session.type === 'other' ? (
              <Field label="Name this type" required>
                <Input value={session.customType} onChange={(e) => setSession({ ...session, customType: e.target.value })}
                  placeholder="e.g. Team Building" />
              </Field>
            ) : <div />}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Starts at" required>
              <Input type="datetime-local" value={session.startAt} onChange={(e) => setSession({ ...session, startAt: e.target.value })} />
            </Field>
            <Field label="Ends at">
              <Input type="datetime-local" value={session.endAt} onChange={(e) => setSession({ ...session, endAt: e.target.value })} />
            </Field>
          </div>
          <Field
            label="When attendance can be marked"
            hint="Leave blank to open 30 minutes before the start and close 30 minutes after the end. Times are Indian time."
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
              <Input type="datetime-local" value={session.attendanceOpensAt}
                onChange={(e) => setSession({ ...session, attendanceOpensAt: e.target.value })} />
              <Input type="datetime-local" value={session.attendanceClosesAt}
                onChange={(e) => setSession({ ...session, attendanceClosesAt: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 8 }}>
              {[[0, 'From the start time'], [15, '15 min before'], [30, '30 min before'], [60, '1 hour before']].map(([mins, label]) => (
                <button key={mins} type="button" disabled={!session.startAt}
                  onClick={() => setSession({ ...session, attendanceOpensAt: shiftLocal(session.startAt, -mins) })}
                  style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 99, border: '1px solid var(--border)', color: 'var(--text-mid)', opacity: session.startAt ? 1 : 0.5 }}>
                  {label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Description" hint="Shown under the row in the attendee's schedule">
            <Textarea value={session.description} onChange={(e) => setSession({ ...session, description: e.target.value })}
              placeholder="What happens in this slot…" />
          </Field>

          <Field
            label="Extra details"
            hint="Add whatever this session needs — a dress code, a headcount, a floor plan"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {session.customFields.map((f, i) => (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 108px minmax(0,1.2fr) 30px',
                  gap: 8, alignItems: 'center',
                }}>
                  <Input
                    value={f.label} placeholder="Field name"
                    onChange={(e) => setCustomField(i, { label: e.target.value })}
                  />
                  <Select value={f.type} onChange={(e) => setCustomField(i, { type: e.target.value, value: '' })}>
                    {CUSTOM_FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </Select>

                  {f.type === 'image' ? (
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                      padding: '9px 11px', fontSize: 12.5, borderRadius: 'var(--r)',
                      border: '1px dashed var(--border)', color: 'var(--text-mid)',
                      overflow: 'hidden', whiteSpace: 'nowrap',
                    }}>
                      <input type="file" accept="image/*" hidden
                        onChange={(e) => uploadFieldImage(i, e.target.files?.[0])} />
                      {f.uploading ? 'Uploading…' : f.value
                        ? <><Check size={13} color="var(--ok)" /> Image added</>
                        : 'Choose image'}
                    </label>
                  ) : (
                    <Input
                      type={f.type === 'number' ? 'number' : 'text'}
                      value={f.value} placeholder="Value"
                      onChange={(e) => setCustomField(i, { value: e.target.value })}
                    />
                  )}

                  <button type="button" onClick={() => removeCustomField(i)} title="Remove field"
                    style={{ color: 'var(--text-lo)', display: 'flex', padding: 6, borderRadius: 6 }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}

              <Button variant="outline" size="sm" icon={Plus} onClick={addCustomField}
                style={{ alignSelf: 'flex-start' }}>
                Add field
              </Button>
            </div>
          </Field>
          <Field label="Ask attendees if they will come" hint="Their answer is final and you can close responses at any time">
            <div style={{
              border: '1px solid var(--border)', borderRadius: 'var(--r)',
              padding: 13, display: 'flex', flexDirection: 'column', gap: 13,
              background: session.attendancePoll.enabled ? 'rgba(239,212,138,0.04)' : 'transparent',
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="checkbox" checked={session.attendancePoll.enabled}
                  onChange={(e) => setPoll({ enabled: e.target.checked })}
                  style={{ accentColor: 'var(--gold-400)' }}
                />
                Take responses for this slot
              </label>

              {session.attendancePoll.enabled && (
                <>
                  <Field label="Question">
                    <Input
                      value={session.attendancePoll.question}
                      onChange={(e) => setPoll({ question: e.target.value })}
                      placeholder="e.g. Will you join us for dinner?"
                    />
                  </Field>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
                    <Field label="Answer type">
                      <Select value={session.attendancePoll.type}
                        onChange={(e) => setPoll({ type: e.target.value })}>
                        <option value="yes_no">Yes / No</option>
                        <option value="choice">Choose from options</option>
                      </Select>
                    </Field>
                    {session.attendancePoll.type === 'choice' && (
                      <Field label="How many">
                        <Select value={session.attendancePoll.multi ? 'multi' : 'single'}
                          onChange={(e) => setPoll({ multi: e.target.value === 'multi' })}>
                          <option value="single">One answer</option>
                          <option value="multi">Several answers</option>
                        </Select>
                      </Field>
                    )}
                  </div>

                  {session.attendancePoll.type === 'choice' && (
                    <Field label="Options" hint="One per line, or comma separated">
                      <Textarea
                        value={session.attendancePoll.options.join('\n')}
                        onChange={(e) => setPoll({ options: e.target.value.split(/[\n,]/) })}
                        placeholder={'Kayaking\nTrekking\nNot attending'}
                      />
                    </Field>
                  )}

                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                    <input
                      type="checkbox" checked={session.attendancePoll.allowNote}
                      onChange={(e) => setPoll({ allowNote: e.target.checked })}
                      style={{ accentColor: 'var(--gold-400)' }}
                    />
                    Let them add a short note
                  </label>

                  {session.attendancePoll.allowNote && (
                    <Field label="Note prompt" hint="Optional">
                      <Input
                        value={session.attendancePoll.noteLabel}
                        onChange={(e) => setPoll({ noteLabel: e.target.value })}
                        placeholder="e.g. Dietary needs?"
                      />
                    </Field>
                  )}
                </>
              )}
            </div>
          </Field>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={session.isMandatory}
              onChange={(e) => setSession({ ...session, isMandatory: e.target.checked })}
              style={{ width: 15, height: 15, accentColor: 'var(--gold-400)' }} />
            Attendance is mandatory for this slot
          </label>
        </div>
      </Modal>

      <Modal
        open={viewerModal} onClose={() => setViewerModal(false)}
        title="Add a company observer"
        subtitle="They sign in to the web portal and see this event, read-only"
        footer={
          <>
            <Button variant="subtle" onClick={() => setViewerModal(false)}>Cancel</Button>
            <Button onClick={addViewer} loading={saving}>Create account</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <Field label="Full name" required>
            <Input value={viewer.name} onChange={(e) => setViewer({ ...viewer, name: e.target.value })} autoFocus />
          </Field>
          <Field label="Email" required hint="This is their login">
            <Input type="email" value={viewer.email} onChange={(e) => setViewer({ ...viewer, email: e.target.value })} />
          </Field>
          <Field label="Password" required>
            <Input type="password" autoComplete="new-password" value={viewer.password}
              onChange={(e) => setViewer({ ...viewer, password: e.target.value })}
              placeholder="At least 6 characters" />
          </Field>
          <Field label="Mobile" hint="Optional, for contact only">
            <Input value={viewer.mobile} onChange={(e) => setViewer({ ...viewer, mobile: e.target.value })} />
          </Field>
        </div>
      </Modal>

      <Modal
        open={formModal} onClose={() => setFormModal(false)}
        title={formDraft?._id ? 'Edit form' : 'Build a form'}
        subtitle="Questions attendees answer in the app, or through a public link"
        width={720}
        footer={(
          <>
            <Button variant="subtle" onClick={() => setFormModal(false)}>Cancel</Button>
            <Button onClick={saveForm} loading={saving}>Save form</Button>
          </>
        )}
      >
        {formDraft && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.6fr) minmax(0,1fr)', gap: 13 }}>
              <Field label="Form title" required>
                <Input value={formDraft.title} autoFocus placeholder="e.g. Arrival details"
                  onChange={(e) => setFormDraft({ ...formDraft, title: e.target.value })} />
              </Field>
              <Field label="Where it appears">
                <Select value={formDraft.placement}
                  onChange={(e) => setFormDraft({ ...formDraft, placement: e.target.value })}>
                  <option value="app_home">App home screen</option>
                  <option value="attendance">Asked at check-in (attendance)</option>
                  <option value="registration">Public registration link</option>
                  <option value="rooming">Rooming questionnaire</option>
                </Select>
              </Field>
            </div>

            <Field label="Description" hint="Shown above the questions">
              <Textarea value={formDraft.description}
                onChange={(e) => setFormDraft({ ...formDraft, description: e.target.value })}
                placeholder="Why you're asking…" />
            </Field>

            {formDraft.placement === 'attendance' && (
              <div style={{ padding: 14, background: 'var(--ink-900)', border: '1px solid var(--border)', borderRadius: 'var(--r)', display: 'flex', flexDirection: 'column', gap: 13 }}>
                <p style={{ fontSize: 12.5, color: 'var(--text-mid)', lineHeight: 1.7 }}>
                  This is the form an attendee answers as they scan in. It needs a question for whichever detail
                  identifies them — mapped to that value and marked required — and a question mapped to
                  <strong> People along with them</strong>, which is what counts their party.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 13 }}>
                  <Field label="What identifies them?" required hint="Has to match what the roster holds — and be unique">
                    <Select value={formDraft.attendance?.identifier || 'employeeCode'}
                      onChange={(e) => setFormDraft({ ...formDraft, attendance: { ...formDraft.attendance, identifier: e.target.value } })}>
                      <option value="employeeCode">Employee id</option>
                      <option value="mobile">Mobile number</option>
                      <option value="email">Email address</option>
                    </Select>
                  </Field>
                  <Field label="Most people they may bring" hint="0 for no limit">
                    <Input type="number" min="0" value={formDraft.attendance?.maxCompanions ?? 0}
                      onChange={(e) => setFormDraft({ ...formDraft, attendance: { ...formDraft.attendance, maxCompanions: Number(e.target.value) || 0 } })} />
                  </Field>
                </div>
                <label style={{ display: 'flex', gap: 9, alignItems: 'center', fontSize: 13 }}>
                  <input type="checkbox" style={{ accentColor: 'var(--gold-400)' }}
                    checked={Boolean(formDraft.attendance?.requireCompanions)}
                    onChange={(e) => setFormDraft({ ...formDraft, attendance: { ...formDraft.attendance, requireCompanions: e.target.checked } })} />
                  They must say how many people are with them before they can check in
                </label>
                <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13 }}>
                  <input type="checkbox" style={{ accentColor: 'var(--gold-400)', marginTop: 3 }}
                    checked={Boolean(formDraft.attendance?.allowWalkIns)}
                    onChange={(e) => setFormDraft({ ...formDraft, attendance: { ...formDraft.attendance, allowWalkIns: e.target.checked } })} />
                  <span>
                    Somebody who is not on the uploaded sheet may add themselves
                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-lo)', marginTop: 3, lineHeight: 1.6 }}>
                      They join this event's people marked <strong>added at check-in</strong>, so the Users list can be
                      filtered and downloaded with or without them. Needs a question mapped to "Name".
                    </span>
                  </span>
                </label>
              </div>
            )}

            {formDraft.placement === 'registration' && (
              <Field
                label="How will they sign in?"
                required
                hint="Whichever you pick must be a required question, mapped to that value"
              >
                <Select value={formDraft.loginIdentifier}
                  onChange={(e) => setFormDraft({ ...formDraft, loginIdentifier: e.target.value })}>
                  <option value="mobile">Mobile number — WhatsApp OTP, like every attendee</option>
                  <option value="email">Email — a login ID and password are generated and emailed</option>
                </Select>
              </Field>
            )}

            <Field label="Questions" required>
              <FormBuilder
                fields={formDraft.fields}
                onChange={(fields) => setFormDraft({ ...formDraft, fields })}
              />
            </Field>
          </div>
        )}
      </Modal>

      <Modal
        open={alertModal} onClose={() => setAlertModal(false)}
        title={alertDraft?._id ? 'Edit home alert' : 'New home alert'}
        subtitle="This is the card on the attendee's home screen"
        footer={(
          <>
            <Button variant="subtle" onClick={() => setAlertModal(false)}>Cancel</Button>
            <Button onClick={saveAlert} loading={saving}>Save alert</Button>
          </>
        )}
      >
        {alertDraft && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            <Field label="Title" required>
              <Input value={alertDraft.title} autoFocus placeholder="e.g. Confirm your Day 1 plans"
                onChange={(e) => setAlertDraft({ ...alertDraft, title: e.target.value })} />
            </Field>
            <Field label="Message" hint="A line of context under the title">
              <Textarea value={alertDraft.message}
                onChange={(e) => setAlertDraft({ ...alertDraft, message: e.target.value })}
                placeholder="Tell us which sessions you'll join…" />
            </Field>

            <Field label="What should it show?" required>
              <Select value={alertDraft.kind}
                onChange={(e) => setAlertDraft({ ...alertDraft, kind: e.target.value, form: '', scheduleDate: '' })}>
                <option value="form">A form to fill in</option>
                <option value="schedule">A day's schedule to confirm</option>
              </Select>
            </Field>

            {alertDraft.kind === 'form' ? (
              <Field label="Which form" required hint="Only published forms reach the app">
                <Select value={alertDraft.form}
                  onChange={(e) => setAlertDraft({ ...alertDraft, form: e.target.value })}>
                  <option value="">Choose a form…</option>
                  {(forms || []).map((f) => (
                    <option key={f._id} value={f._id}>
                      {f.title}{f.status === 'published' ? '' : ` (${f.status})`}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : (
              <Field label="Which day" required>
                <Select value={alertDraft.scheduleDate}
                  onChange={(e) => setAlertDraft({ ...alertDraft, scheduleDate: e.target.value })}>
                  <option value="">Choose a day…</option>
                  {days.map((d) => (
                    <option key={d.date} value={d.date}>
                      {fmtDate(d.date)} · {d.sessions.length} item{d.sessions.length === 1 ? '' : 's'}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
              <Field label="Show from" required>
                <Input type="date" value={alertDraft.showOn}
                  onChange={(e) => setAlertDraft({ ...alertDraft, showOn: e.target.value })} />
              </Field>
              <Field label="Stop showing" hint="Optional">
                <Input type="date" value={alertDraft.showUntil ? alertDraft.showUntil.slice(0, 10) : ''}
                  onChange={(e) => setAlertDraft({ ...alertDraft, showUntil: e.target.value })} />
              </Field>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(qrFor)} onClose={() => setQrFor(null)}
        title={qrFor?.form?.placement === 'attendance' ? 'Check-in code for the desk' : 'Share this registration form'}
        subtitle={qrFor?.form?.title}
        width={430}
      >
        {qrFor?.loading ? <Loader label="Generating the code" /> : qrFor && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'inline-block', padding: 16, background: '#fff', borderRadius: 'var(--r-lg)' }}>
              <img src={qrFor.image} alt={qrFor.form?.placement === 'attendance' ? 'Check-in QR' : 'Registration QR'}
                style={{ width: 210, height: 210, display: 'block' }} />
            </div>

            <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 16, lineHeight: 1.6 }}>
              {qrFor.form.placement === 'attendance' ? (
                <>
                  Put this on the welcome desk. Anyone can scan it with their own camera and check in from
                  the page it opens — no app, no sign-in. They are recognised by their{' '}
                  {{ employeeCode: 'employee id', mobile: 'mobile number', email: 'email address' }[qrFor.form.attendance?.identifier || 'employeeCode']},
                  which has to be on this event's list already.
                </>
              ) : (
                <>
                  Print it, or share the link. Anyone who scans it can register —
                  {qrFor.form.loginIdentifier === 'email'
                    ? ' they will be emailed a login ID and password.'
                    : ' they will sign in to the app with a WhatsApp OTP.'}
                </>
              )}
            </p>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 9, marginTop: 14,
              padding: '10px 12px', background: 'var(--ink-900)',
              border: '1px solid var(--border)', borderRadius: 'var(--r)',
            }}>
              <span style={{
                flex: 1, minWidth: 0, fontSize: 12, textAlign: 'left',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                color: 'var(--gold-300)',
              }}>
                {qrFor.url}
              </span>
              <button
                onClick={() => { navigator.clipboard?.writeText(qrFor.url); setCopied(true); setTimeout(() => setCopied(false), 1600); }}
                title="Copy link"
                style={{ color: 'var(--text-mid)', display: 'flex', padding: 5 }}
              >
                {copied ? <Check size={15} color="var(--ok)" /> : <Copy size={15} />}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(issued)} onClose={() => setIssued(null)}
        title="Sign-in details"
        subtitle={issued?.name}
        width={400}
        footer={<Button onClick={() => setIssued(null)}>Done</Button>}
      >
        {issued && (
          <>
            <div style={{
              padding: 14, background: 'var(--ink-900)',
              border: '1px solid var(--border)', borderRadius: 'var(--r)', fontSize: 13,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ color: 'var(--text-lo)' }}>Login ID</span>
                <strong style={{ color: 'var(--gold-400)', wordBreak: 'break-all' }}>{issued.loginId}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 10 }}>
                <span style={{ color: 'var(--text-lo)' }}>Password</span>
                <strong style={{ color: 'var(--gold-400)', fontFamily: 'monospace', fontSize: 14 }}>
                  {issued.password}
                </strong>
              </div>
            </div>

            <p style={{ fontSize: 12, color: 'var(--warn)', marginTop: 13, lineHeight: 1.7 }}>
              This is the only time this password is shown. It has been sent to them
              {issued.sent ? '' : ' — sending was skipped'}; copy it now if you need it.
            </p>
            <Button
              variant="outline" size="sm" icon={copied ? Check : Copy}
              onClick={() => {
                navigator.clipboard?.writeText(`${issued.loginId} / ${issued.password}`);
                setCopied(true); setTimeout(() => setCopied(false), 1600);
              }}
              style={{ marginTop: 12 }}
            >
              {copied ? 'Copied' : 'Copy both'}
            </Button>
          </>
        )}
      </Modal>

      <Modal
        open={hotelModal} onClose={() => setHotelModal(false)}
        title="Assign hotels"
        subtitle="Tick the properties hosting this event, and optionally plan how many rooms of each size are held"
        width={620}
        footer={(
          <>
            <Button variant="subtle" onClick={() => setHotelModal(false)}>Cancel</Button>
            <Button onClick={saveHotels} loading={saving}>Save</Button>
          </>
        )}
      >
        {!allHotels?.length ? (
          <EmptyState
            message="No hotels on file yet"
            hint="Add a property under Hotels first, then come back to assign it."
            icon={BedDouble}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {allHotels.map((h) => {
              const on = pickedHotels.includes(h._id);
              return (
                <div key={h._id} style={{
                  border: `1px solid ${on ? 'rgba(239,212,138,0.32)' : 'var(--border)'}`,
                  borderRadius: 'var(--r)', overflow: 'hidden',
                  background: on ? 'rgba(239,212,138,0.05)' : 'transparent',
                }}>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 11,
                    padding: '12px 13px', cursor: 'pointer',
                  }}>
                    <input type="checkbox" checked={on} onChange={() => toggleHotel(h._id)}
                      style={{ accentColor: 'var(--gold-400)' }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 500, display: 'block' }}>{h.name}</span>
                      <span style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>
                        {[h.address?.city, h.address?.state].filter(Boolean).join(', ') || 'No address on file'}
                      </span>
                    </span>
                  </label>

                  {/* The capacity plan only matters once the hotel is on the event. */}
                  {on && (
                    <div style={{ padding: '0 13px 13px' }}>
                      <p style={{ fontSize: 11.5, color: 'var(--text-lo)', marginBottom: 8, lineHeight: 1.6 }}>
                        How many rooms of each size are held here? Optional — it is
                        only to size the event, the real allocation happens later.
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 9 }}>
                        {OCCUPANCIES.map((occ) => (
                          <Field key={occ} label={`${occ}-person`}>
                            <Input
                              type="number" min={0} placeholder="0"
                              value={invFor(h._id, occ)}
                              onChange={(e) => setInv(h._id, occ, e.target.value)}
                            />
                          </Field>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(responsesFor)} onClose={() => setResponsesFor(null)}
        title="Responses"
        subtitle={responsesFor?.title}
        width={900}
        footer={(
          <>
            <Button variant="subtle" onClick={() => setResponsesFor(null)}>Close</Button>
            <Button icon={Download} onClick={() => downloadResponses(responsesFor)}>
              Download Excel
            </Button>
          </>
        )}
      >
        {responsesLoading ? <Loader label="Loading responses" /> : !responses?.rows?.length ? (
          <EmptyState message="Nobody has answered yet" icon={ClipboardList} />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 560 }}>
              <thead>
                <tr>
                  {['Person', 'Contact', ...(responses.form?.fields || []).map((f) => f.label),
                    ...(responsesFor?.placement === 'attendance' ? ['Against the sheet'] : []), 'Submitted'].map((h) => (
                    <th key={h} style={{
                      textAlign: 'left', padding: '9px 11px', whiteSpace: 'nowrap',
                      borderBottom: '1px solid var(--border)', color: 'var(--text-lo)',
                      fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 600,
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {responses.rows.map((r) => (
                  <tr key={r._id}>
                    <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)' }}>
                      <div style={{ fontWeight: 500 }}>{r.user?.name || 'Unknown'}</div>
                      {r.user?.employeeCode && (
                        <div style={{ fontSize: 11, color: 'var(--text-lo)', marginTop: 2 }}>{r.user.employeeCode}</div>
                      )}
                    </td>
                    <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)', color: 'var(--text-mid)' }}>
                      <div>{r.user?.email || '—'}</div>
                      {r.user?.mobile && (
                        <div style={{ fontSize: 11, color: 'var(--text-lo)', marginTop: 2 }}>{r.user.mobile}</div>
                      )}
                    </td>
                    {(responses.form?.fields || []).map((f) => {
                      const v = r.responses?.[f.key];
                      const text = Array.isArray(v) ? v.join(', ')
                        : typeof v === 'boolean' ? (v ? 'Yes' : 'No')
                        : v ?? '';
                      const isLink = typeof text === 'string' && /^https?:\/\//.test(text);
                      return (
                        <td key={f.key} style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)' }}>
                          {isLink
                            ? <a href={text} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold-300)' }}>Open file</a>
                            : (text || <span style={{ color: 'var(--text-lo)' }}>—</span>)}
                        </td>
                      );
                    })}
                    {responsesFor?.placement === 'attendance' && (
                      <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)' }}>
                        <PartyTag row={party.byUser[String(r.user?._id)]} />
                        {!isViewer && r.user && (
                          <CheckInActions
                            eventId={id} person={r.user} row={party.byUser[String(r.user._id)]} compact
                            onChange={() => { party.reload(); reloadResponses(); reloadPeople(); }}
                          />
                        )}
                      </td>
                    )}
                    <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)', color: 'var(--text-lo)', whiteSpace: 'nowrap' }}>
                      {fmtDate(r.submittedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(pollFor)} onClose={() => setPollFor(null)}
        title="Responses"
        subtitle={pollData?.session?.title}
        width={760}
        footer={pollData && (
          <>
            <Button variant="subtle" onClick={() => setPollFor(null)}>Close</Button>
            <Button
              variant="subtle"
              onClick={() => setPollClosed(pollFor, !pollData.session.closed)}
              style={{ marginRight: 'auto' }}
            >
              {pollData.session.closed ? 'Reopen responses' : 'Close responses'}
            </Button>
            <Button icon={Download} onClick={() => downloadPollResponses(pollFor, pollData.session.title)}>
              Download Excel
            </Button>
          </>
        )}
      >
        {pollLoading || !pollData ? <Loader label="Loading responses" /> : (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 14 }}>
              {pollData.session.poll?.question || 'Will you attend?'}
            </p>

            <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginBottom: 16 }}>
              <Badge tone="success" dot>{pollData.responded} answered</Badge>
              <Badge tone="neutral">{pollData.awaiting} awaiting</Badge>
              <Badge tone={pollData.session.closed ? 'danger' : 'gold'}>
                {pollData.session.closed ? 'Closed' : 'Open'}
              </Badge>
              {Object.entries(pollData.tally).map(([k, n]) => (
                <Badge key={k} tone="info">{k}: {n}</Badge>
              ))}
            </div>

            {!pollData.rows.length ? (
              <EmptyState message="Nobody has answered yet" icon={Users} />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      {['Person', 'Contact', 'Answer',
                        ...(pollData.session.poll?.allowNote ? [pollData.session.poll.noteLabel || 'Note'] : []),
                        'Answered'].map((h) => (
                        <th key={h} style={{
                          textAlign: 'left', padding: '9px 11px', whiteSpace: 'nowrap',
                          borderBottom: '1px solid var(--border)', color: 'var(--text-lo)',
                          fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 600,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pollData.rows.map((r) => (
                      <tr key={r._id}>
                        <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)' }}>
                          <div style={{ fontWeight: 500 }}>{r.user?.name || 'Unknown'}</div>
                          {r.user?.employeeCode && (
                            <div style={{ fontSize: 11, color: 'var(--text-lo)', marginTop: 2 }}>{r.user.employeeCode}</div>
                          )}
                        </td>
                        <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)', color: 'var(--text-mid)' }}>
                          {r.user?.email || r.user?.mobile || '—'}
                        </td>
                        <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)' }}>
                          {r.choices?.length
                            ? <span style={{ color: 'var(--gold-300)' }}>{r.choices.join(', ')}</span>
                            : <Badge tone={r.attending ? 'success' : 'neutral'}>{r.attending ? 'Yes' : 'No'}</Badge>}
                        </td>
                        {pollData.session.poll?.allowNote && (
                          <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)', color: 'var(--text-mid)' }}>
                            {r.note || <span style={{ color: 'var(--text-lo)' }}>—</span>}
                          </td>
                        )}
                        <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)', color: 'var(--text-lo)', whiteSpace: 'nowrap' }}>
                          {fmtDate(r.respondedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* ── health & emergency ───────────────────────────────────── */}
      {tab === 'health' && (
        <HealthTab eventId={id} event={event} onSaved={reload} readOnly={isViewer} />
      )}

      {liveFor && (
        <LiveScanFeed
          eventId={id}
          sessionId={liveFor._id}
          title={`Live scans · ${liveFor.title}`}
          readOnly={isViewer}
          onClose={() => setLiveFor(null)}
        />
      )}

      {scanFor && <SessionScanModal eventId={id} session={scanFor} onClose={() => { setScanFor(null); if (attendanceFor) setAttendanceFor(null); }} />}

      <Modal
        open={Boolean(sessionQrFor)} onClose={() => setSessionQrFor(null)}
        title="Session code" subtitle={sessionQr?.session?.title} width={430}
      >
        {sessionQrLoading || !sessionQr ? <Loader label="Generating the code" /> : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'inline-block', padding: 16, background: '#fff', borderRadius: 'var(--r-lg)' }}>
              <img src={sessionQr.image} alt="Session QR" style={{ width: 220, height: 220, display: 'block' }} />
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 16, lineHeight: 1.7 }}>
              Print this or show it at the door. Attendees scan it from the app to
              mark themselves present — or staff scan their badge instead. Either
              way it lands in the same list.
            </p>
            <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 10 }}>
              Scanning opens {fmtDateTime(sessionQr.opensAt)} and closes {fmtDateTime(sessionQr.closesAt)}.
            </p>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(attendanceFor)} onClose={() => setAttendanceFor(null)}
        title="Attendance" subtitle={attendance?.session?.title} width={720}
        footer={attendance && (
          <>
            <Button variant="subtle" onClick={() => setAttendanceFor(null)}>Close</Button>
            <Button icon={Download} onClick={() => downloadAttendance(attendanceFor, attendance.session.title)}>
              Download Excel
            </Button>
          </>
        )}
      >
        {attendanceLoading || !attendance ? <Loader label="Loading attendance" /> : (
          <>
            <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginBottom: 16 }}>
              <Badge tone="success" dot>{attendance.present} present</Badge>
              <Badge tone="neutral">{attendance.absent} absent</Badge>
              <Badge tone="info">{attendance.selfScanned} self-scanned</Badge>
              <Badge tone="gold">{attendance.expected} expected</Badge>
            </div>

            {!attendance.rows.length ? (
              <EmptyState message="Nobody has been scanned in yet" icon={QrCode} />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      {['Person', 'Contact', 'Marked by', 'Scanned at'].map((h) => (
                        <th key={h} style={{
                          textAlign: 'left', padding: '9px 11px', whiteSpace: 'nowrap',
                          borderBottom: '1px solid var(--border)', color: 'var(--text-lo)',
                          fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 600,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.rows.map((r) => (
                      <tr key={r._id}>
                        <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)' }}>
                          <div style={{ fontWeight: 500 }}>{r.user?.name || 'Unknown'}</div>
                          {r.user?.employeeCode && (
                            <div style={{ fontSize: 11, color: 'var(--text-lo)', marginTop: 2 }}>{r.user.employeeCode}</div>
                          )}
                        </td>
                        <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)', color: 'var(--text-mid)' }}>
                          {r.user?.email || r.user?.mobile || '—'}
                        </td>
                        <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)' }}>
                          {r.selfScanned
                            ? <Badge tone="info">Self scan</Badge>
                            : <span style={{ color: 'var(--text-mid)' }}>{r.scannedBy?.name || 'Staff'}</span>}
                        </td>
                        <td style={{ padding: '10px 11px', borderBottom: '1px solid var(--ink-750)', color: 'var(--text-lo)', whiteSpace: 'nowrap' }}>
                          {fmtDateTime(r.scannedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Modal>

      <BroadcastModal open={broadcast} onClose={() => setBroadcast(false)} eventId={id} />

      <ConfirmModal
        open={Boolean(confirmDel)} onClose={() => setConfirmDel(null)} onConfirm={removeSession}
        title="Remove this session?" danger confirmLabel="Remove"
        message="It will disappear from every attendee's schedule. Attendance already recorded against it is kept."
      />
    </motion.div>
  );
}

/**
 * The health and emergency questions an attendee answers after signing in:
 * the switches for this event, and everyone's answers.
 */
function HealthTab({ eventId, event, onSaved, readOnly }) {
  const toast = useToast();
  const d = event.declaration || {};
  const [f, setF] = useState({
    enabled: Boolean(d.enabled),
    mandatory: d.mandatory !== false,
    askChronic: d.askChronic !== false,
    askEmergency: d.askEmergency !== false,
    requireLiability: d.requireLiability !== false,
    title: d.title || '',
    note: d.note || '',
    liabilityText: d.liabilityText || '',
  });
  const [saving, setSaving] = useState(false);
  const { data, loading, reload } = useFetch('/declarations', { event: eventId });

  const save = async (patch = {}) => {
    const next = { ...f, ...patch };
    setF(next);
    setSaving(true);
    try {
      await patch(`/events/${eventId}`, { declaration: next });
      toast.success('Saved');
      onSaved();
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  const download = async () => {
    try {
      await download('/declarations/export', { event: eventId },
        `${event.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-health-declarations.xlsx`);
    } catch (err) { toast.error(err.message); }
  };

  const rows = data?.rows || [];
  const summary = data?.summary;

  return (
    <>
      <SectionTitle
        title="Health & emergency details"
        subtitle="Asked once, right after the attendee signs in"
        action={rows.length ? <Button variant="outline" size="sm" icon={Download} onClick={download}>Excel</Button> : null}
      />

      <Card padding={20} style={{ marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 11, cursor: readOnly ? 'default' : 'pointer' }}>
          <input type="checkbox" checked={f.enabled} disabled={readOnly || saving}
            onChange={(e) => save({ enabled: e.target.checked })}
            style={{ accentColor: 'var(--gold-400)', width: 16, height: 16, marginTop: 2 }} />
          <span>
            <span style={{ fontSize: 14.5, fontWeight: 600 }}>Ask for these details on this event</span>
            <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-lo)', marginTop: 3, lineHeight: 1.6 }}>
              Any ongoing medical condition, who to call in an emergency, and the attendee's acceptance that their own health
              stays their responsibility on the trip.
            </span>
          </span>
        </label>

        {f.enabled && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 11 }}>
            {[
              ['mandatory', 'Must be answered before anything else opens', 'Without this they can skip it and fill it in later from their profile.'],
              ['askChronic', 'Ask about ongoing medical conditions', 'Including what medication they carry, and any allergies.'],
              ['askEmergency', 'Ask who to contact in an emergency', 'Name, relationship and phone number.'],
              ['requireLiability', 'Require the responsibility tick', 'They cannot submit without ticking it.'],
            ].map(([key, label, hint]) => (
              <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 11, cursor: readOnly ? 'default' : 'pointer' }}>
                <input type="checkbox" checked={f[key]} disabled={readOnly || saving}
                  onChange={(e) => save({ [key]: e.target.checked })}
                  style={{ accentColor: 'var(--gold-400)', width: 15, height: 15, marginTop: 2 }} />
                <span>
                  <span style={{ fontSize: 13.5 }}>{label}</span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--text-lo)', marginTop: 2 }}>{hint}</span>
                </span>
              </label>
            ))}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginTop: 6 }}>
              <Field label="Heading" hint='Default: "Before you travel"'>
                <Input value={f.title} disabled={readOnly} onChange={(e) => setF({ ...f, title: e.target.value })} onBlur={() => save()} />
              </Field>
              <Field label="Note above the questions" hint="Optional">
                <Input value={f.note} disabled={readOnly} onChange={(e) => setF({ ...f, note: e.target.value })} onBlur={() => save()} />
              </Field>
            </div>
            <Field label="Wording of the responsibility tick" hint="Leave blank for the standard wording">
              <Textarea rows={3} value={f.liabilityText} disabled={readOnly}
                onChange={(e) => setF({ ...f, liabilityText: e.target.value })} onBlur={() => save()}
                placeholder={data?.settings?.liabilityText} />
            </Field>
          </div>
        )}
      </Card>

      {f.enabled && summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 13, marginBottom: 16 }}>
          <Stat label="Answered" value={summary.answered} icon={CheckCircle2} tone="success" />
          <Stat label="Still to answer" value={summary.pending} icon={Clock} tone={summary.pending ? 'warning' : 'neutral'} />
          <Stat label="Declared a condition" value={summary.withCondition} icon={HeartPulse} tone="gold" />
        </div>
      )}

      {f.enabled && (
        <Table
          loading={loading}
          rows={rows}
          keyField="user"
          empty="Nobody has answered yet"
          columns={[
            { key: 'person', title: 'Person', render: (r) => <PersonCell person={r.user} /> },
            { key: 'state', title: 'Answered', render: (r) => (r.declaration
              ? <Badge tone="success">{fmtDate(r.declaration.submittedAt)}</Badge>
              : <Badge tone="warning">Pending</Badge>) },
            { key: 'condition', title: 'Medical condition', render: (r) => {
              if (!r.declaration) return '—';
              if (!r.declaration.chronic?.has) return <span style={{ color: 'var(--text-lo)' }}>None declared</span>;
              return (
                <div style={{ maxWidth: 260 }}>
                  <p style={{ fontSize: 13, color: 'var(--warn)' }}>{r.declaration.chronic.details}</p>
                  {r.declaration.chronic.medication && <p style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>Carries: {r.declaration.chronic.medication}</p>}
                </div>
              );
            } },
            { key: 'allergies', title: 'Allergies', render: (r) => r.declaration?.chronic?.allergies || '—' },
            { key: 'emergency', title: 'Emergency contact', render: (r) => (r.declaration?.emergency?.name ? (
              <div>
                <p style={{ fontSize: 13 }}>{r.declaration.emergency.name}{r.declaration.emergency.relation ? ` · ${r.declaration.emergency.relation}` : ''}</p>
                <a href={`tel:${r.declaration.emergency.mobile}`} style={{ fontSize: 12, color: 'var(--gold-300)' }}>{r.declaration.emergency.mobile}</a>
              </div>
            ) : '—') },
            { key: 'accepted', title: 'Accepted', align: 'center', render: (r) => (r.declaration
              ? (r.declaration.liabilityAccepted ? <CheckCircle2 size={15} color="var(--ok)" /> : <XCircle size={15} color="var(--danger)" />)
              : '—') },
          ]}
        />
      )}
    </>
  );
}

function Row({ label: text, value }) {
  return (
    <div>
      <p style={{ fontSize: 11, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{text}</p>
      <div style={{ fontSize: 13.5, marginTop: 4 }}>{value}</div>
    </div>
  );
}

/** Groups sessions into calendar days, sorted chronologically. */
function groupByDay(sessions) {
  const map = new Map();
  [...sessions]
    .sort((a, b) => new Date(a.startAt) - new Date(b.startAt))
    .forEach((s) => {
      const key = new Date(s.startAt).toISOString().slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
  return [...map.entries()];
}

export function BroadcastModal({ open, onClose, eventId }) {
  const toast = useToast();
  const [form, setForm] = useState({ title: '', body: '', audience: 'all', priority: 'normal' });
  const [channels, setChannels] = useState(['in_app', 'push']);
  const [busy, setBusy] = useState(false);

  const toggle = (c) => setChannels((cur) => cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]);

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error('Add a title and a message');
    if (!channels.length) return toast.error('Pick at least one channel');
    setBusy(true);
    try {
      const res = await post(`/events/${eventId}/broadcast`, { ...form, channels });
      toast.success(res.message);
      onClose();
      setForm({ title: '', body: '', audience: 'all', priority: 'normal' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open} onClose={onClose}
      title="Broadcast to participants"
      subtitle="Reaches everyone on this event at once"
      footer={
        <>
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button icon={Megaphone} onClick={send} loading={busy}>Send broadcast</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <Field label="Title" required>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Gala dinner moved to 8 PM" autoFocus />
        </Field>
        <Field label="Message" required>
          <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="Write the announcement…" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
          <Field label="Audience">
            <Select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
              <option value="all">All participants</option>
              <option value="vip">VIP guests only</option>
              <option value="staff">Event staff</option>
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </Select>
          </Field>
        </div>
        <Field label="Channels">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[['in_app', 'In-app'], ['push', 'Push'], ['whatsapp', 'WhatsApp'], ['email', 'Email']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => toggle(v)}
                style={{
                  padding: '7px 14px', borderRadius: 99, fontSize: 12.5, fontWeight: 500,
                  border: `1px solid ${channels.includes(v) ? 'var(--gold-500)' : 'var(--border)'}`,
                  background: channels.includes(v) ? 'rgba(239,212,138,0.13)' : 'transparent',
                  color: channels.includes(v) ? 'var(--gold-300)' : 'var(--text-mid)',
                  transition: 'all 0.18s',
                }}>
                {l}
              </button>
            ))}
          </div>
        </Field>
      </div>
    </Modal>
  );
}
